import { urlDoSelo } from 'components/icons';
import { descreverSelo } from 'pages/Extrato/perfil-calculos';
import { DocumentoDeSelo, LeiturasDoCiclo } from 'services/colecao-de-selos.service';

export type TipoDeSelo = 'cultura' | 'galeria' | 'realizacao';

export interface SeloDaColecao {
  id: string;
  tipo: TipoDeSelo;
  ciclo: string;
  nome: string;
  categoria: string;
  descricao: string;
  /** Arte do selo no ciclo. */
  arte?: string;
  /** Selo com a foto do funci (`selo.url`), regerado pelos triggers de `player` quando a foto muda. */
  imagemPersonalizada?: string;
}

export const TODOS_OS_CICLOS = 'todos';

/** A moldura (o selo com a foto) que o funci vê: só com foto enviada e quando ela existe (AJH-44, AJH-45). */
export function molduraDoSelo(selo: SeloDaColecao, temFoto: boolean) {
  return temFoto ? selo.imagemPersonalizada : undefined;
}

/**
 * AJ2-23: o aviso "Compartilhe sua conquista com a sua foto!" no modo "Com sua foto", quando algum selo visível fica
 * sem moldura: sem foto enviada, ou com foto e a moldura ainda não gerada.
 */
export function pedeFoto(selos: SeloDaColecao[], temFoto: boolean, modo: ModoDaImagem) {
  return modo === 'com-foto' && selos.some(selo => !molduraDoSelo(selo, temFoto));
}

/** AJ2-21: a imagem dos cartões, com a foto (moldura dos triggers) ou só o selo (desenhada no navegador). */
export type ModoDaImagem = 'com-foto' | 'so-o-selo';

/** Nome do arquivo baixado de cada tipo, o mesmo que o `UserBadge` usava. */
export const ARQUIVO_DO_SELO: Record<TipoDeSelo, string> = {
  cultura: 'selo_joga_Junto_destaque_cultura.jpg',
  galeria: 'selo_joga_Junto_galeria.jpg',
  realizacao: 'selo_joga_Junto_realizacao_destaque.jpg',
};

const ORDEM_DOS_TIPOS: TipoDeSelo[] = ['cultura', 'galeria', 'realizacao'];

/** Textos do design por tipo (lacuna SEL-L02: a API não traz descrição de selo). */
export function descricaoDoSelo(tipo: TipoDeSelo, categoria: string) {
  if (tipo === 'cultura') {
    // Sem comportamento reconhecido não há categoria: o texto não cita nenhuma.
    const destaque = categoria ? `em ${categoria} no ciclo` : 'do ciclo';
    return `Você ficou entre os funcis mais reconhecidos ${destaque}. Esse selo celebra quem faz o time avançar junto.`;
  }
  if (tipo === 'galeria') {
    return 'Você ficou entre os 30% mais bem classificados do seu grupo em Pontos Joga Junto, nos níveis Valeu, Mandou bem e UAU. O selo no Humanograma é concedido apenas a quem termina o ciclo no nível UAU.';
  }
  return 'Sua realização foi apreciada e bonificada pela liderança. Um marco do que você entregou no semestre.';
}

/** Lê o título de um ciclo no formato `<semestre>S<ano>` ("1S2026"). Fora do formato, `undefined`. */
export function lerCiclo(ciclo?: string) {
  const achado = ciclo?.match(/^([12])S(\d{4})$/);
  return achado ? { semestre: Number(achado[1]), ano: Number(achado[2]) } : undefined;
}

/** "1S2026" → 20261, "2S2025" → 20252: ordena os ciclos no tempo. Fora do formato vale 0 e vai para o fim. */
function posicaoDoCiclo(ciclo: string) {
  const lido = lerCiclo(ciclo);
  return lido ? lido.ano * 10 + lido.semestre : 0;
}

function montarSelo(tipo: TipoDeSelo, ciclo: string, documento: DocumentoDeSelo, indice: number): SeloDaColecao {
  // `descreverSelo` lê o tipo pelos campos do `get_selos`: comportamento → Cultura, `type` → Realização, nenhum → Galeria.
  const descrito = descreverSelo({
    behavior: tipo === 'cultura' ? documento.behavior : undefined,
    type: tipo === 'realizacao' ? 'realizacao' : undefined,
    cycle: ciclo,
  });
  // Cultura com comportamento desconhecido: `descreverSelo` não acha o selo e cairia em Galeria.
  const culturaSemComportamento = tipo === 'cultura' && descrito.selo === 'Galeria';
  const nome = culturaSemComportamento ? 'Destaque da Cultura' : descrito.nome;
  const categoria = culturaSemComportamento ? '' : descrito.categoria ?? '';

  return {
    // Pelo índice: o `_id` das leituras é a matrícula, igual em todos os documentos do funci.
    id: `${tipo}-${ciclo}-${indice}`,
    tipo,
    ciclo,
    nome,
    categoria,
    descricao: descricaoDoSelo(tipo, categoria),
    arte: culturaSemComportamento ? undefined : urlDoSelo(descrito.selo, ciclo),
    imagemPersonalizada: documento.selo?.url || undefined,
  };
}

/** SEL-03: um selo por documento, do ciclo mais recente ao mais antigo; no ciclo, Cultura, Galeria e Realização. */
export function montarColecao(leituras: LeiturasDoCiclo[]): SeloDaColecao[] {
  return [...leituras]
    .sort((a, b) => posicaoDoCiclo(b.ciclo) - posicaoDoCiclo(a.ciclo))
    .flatMap(leitura => ORDEM_DOS_TIPOS.flatMap(tipo => leitura[tipo].map((documento, indice) => montarSelo(tipo, leitura.ciclo, documento, indice))));
}

/** SEL-05: os ciclos que têm selo, na ordem da coleção. */
export function ciclosComSelo(colecao: SeloDaColecao[]) {
  return Array.from(new Set(colecao.map(selo => selo.ciclo)));
}

export interface OpcaoDoFiltro {
  valor: string;
  rotulo: string;
}

/** AJ2-17: os ciclos que têm selo, do mais recente ao mais antigo, e "Todos os ciclos" por último. */
export function opcoesDoFiltro(colecao: SeloDaColecao[]): OpcaoDoFiltro[] {
  const ciclos = [...ciclosComSelo(colecao)].sort((a, b) => posicaoDoCiclo(b) - posicaoDoCiclo(a));
  return [...ciclos.map(ciclo => ({ valor: ciclo, rotulo: ciclo })), { valor: TODOS_OS_CICLOS, rotulo: 'Todos os ciclos' }];
}

/** AJ2-18: o ciclo mais recente que tem selo; sem selos, "Todos os ciclos". */
export function filtroInicial(colecao: SeloDaColecao[]) {
  return opcoesDoFiltro(colecao)[0].valor;
}

/** SEL-06 */
export function filtrarPorCiclo(colecao: SeloDaColecao[], filtro: string) {
  return filtro === TODOS_OS_CICLOS ? colecao : colecao.filter(selo => selo.ciclo === filtro);
}

/** Meu Perfil: o selo mais recente de cada tipo, na ordem da coleção (AJ2-19). */
export function ultimoDeCadaTipo(colecao: SeloDaColecao[]) {
  const vistos = new Set<TipoDeSelo>();
  return colecao.filter(selo => {
    if (vistos.has(selo.tipo)) return false;
    vistos.add(selo.tipo);
    return true;
  });
}

/** SEL-01 */
export function rotuloDoTotal(total: number) {
  return total === 1 ? '1 selo digital' : `${total} selos digitais`;
}

/** SEL-43 */
export function anuncioDoFiltro(quantidade: number, filtro: string) {
  const selos = quantidade === 1 ? '1 selo' : `${quantidade} selos`;
  return filtro === TODOS_OS_CICLOS ? `${selos} em todos os ciclos` : `${selos} em ${filtro}`;
}

/** SEL-09: "1S2026" → "2S2026"; "2S2025" → "1S2026". Sem o formato `<n>S<ano>`, `undefined`. */
export function proximoCiclo(ciclo?: string) {
  const lido = lerCiclo(ciclo);
  if (!lido) return undefined;

  return lido.semestre === 1 ? `2S${lido.ano}` : `1S${lido.ano + 1}`;
}

/** O mais recente dos ciclos oferecidos (SEL-09). */
export function cicloMaisRecente(ciclos: string[]) {
  return [...ciclos].sort((a, b) => posicaoDoCiclo(b) - posicaoDoCiclo(a))[0];
}
